import { notification } from 'antd';
import type { InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';

import env_constants from '../../internals/env/env_constants.json';
import { extractErrorMessage } from '../utils/notifications';
import { clearStorage } from '../utils/storageHandler';

declare const __API_BASE_URL__: string | undefined;

/**
 * Axios instance configured for Django Session Authentication.
 */
const apiBaseUrl =
  typeof __API_BASE_URL__ !== 'undefined' && __API_BASE_URL__
    ? __API_BASE_URL__
    : env_constants.API_BASE_URL;

const Axios = axios.create({
  baseURL: apiBaseUrl, // every request gets automatically this prefix: a call to /variants/ becomes http://localhost:8055/api/variants/
  withCredentials: true, // critical for session-based authentication (tells the browser to include cookies (the session cookie) with every request) (WO it, the BE wouldn't know who is logged in)
  headers: {
    Accept: 'application/json', // tells BE "please respond in json format"
  },
});

/**
 * 🔐 CSRF FIX (Axios v1 + TypeScript safe)
 * Ensures PATCH/PUT/POST/DELETE always include X-CSRFToken
 */
// Helper function to get the CSRF token from the browsers cookies jar
// The interceptor attaches it as an X-CSRFToken header to every outgoing request
// Django then checks if the header matches the cookie & if yes, the request is trusted
function getCookie(name: string): string | undefined {
  const match = document.cookie.match(
    new RegExp('(^|;\\s*)' + name + '=([^;]*)'),
  );
  return match ? decodeURIComponent(match[2]) : undefined;
}

// The interceptor runs this before EVERY outgoing request
// CSRF = Cross-Site Request forgery (crsf token is a secret cookie set and sent by the BE (as a browser cookie) to the FE to prevent CSRF attacks)
Axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getCookie('csrftoken');

  if (
    !token &&
    config.method &&
    !['get', 'head', 'options'].includes(config.method.toLowerCase())
  ) {
    console.warn(
      `[Axios] CSRF token not found in cookies for ${config.method.toUpperCase()} request to ${config.url}. ` +
        'Ensure the backend sets the "csrftoken" cookie and it is accessible via JS (CSRF_COOKIE_HTTPONLY = False).',
    );
  }

  if (token) {
    // Axios v1 uses AxiosHeaders
    const headers: any = config.headers;

    if (headers && typeof headers.set === 'function') {
      headers.set('X-CSRFToken', token);
    } else {
      config.headers = {
        ...config.headers,
        'X-CSRFToken': token,
      } as any;
    }
  }

  return config;
});

// Centralized trigger to open login modal on unauthorized (no page reload)
const triggerLoginModal = () => {
  try {
    clearStorage();
  } finally {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('app:open-login'));
    }
  }
};

// Response interceptor
Axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status;
    const data = error?.response?.data;

    const errorDescription = extractErrorMessage(
      data,
      error.message || 'Something went wrong.',
    );

    const isUnauthorizedMessage = errorDescription
      .toLowerCase()
      .includes('unauthorized');

    const reqUrl = (originalRequest?.url || '').toString();

    const isAuthEndpoint =
      reqUrl.includes('/_allauth/') || reqUrl.includes('/auth/');

    const isVariantLockEndpoint =
      reqUrl.includes('/variantdata/variant/') && reqUrl.includes('/lock');

    const isExpectedLockedResponse = status === 423 && isVariantLockEndpoint;

    // Detect CSRF failures (often 403) Error handling
    const isCsrfFailure =
      status === 403 &&
      (errorDescription.toLowerCase().includes('csrf') ||
        errorDescription.toLowerCase().includes('cookie not set'));

    const skipErrorToast = Boolean(
      originalRequest?.headers?.['x-skip-error-notification'] ||
      originalRequest?.headers?.['X-Skip-Error-Notification'],
    );

    if (isCsrfFailure) {
      console.error(
        'CSRF Verification Failed. Possible causes:\n' +
          '1. SESSION_COOKIE_SAMESITE/CSRF_COOKIE_SAMESITE is "None" but SECURE is not True.\n' +
          '2. CSRF_COOKIE_HTTPONLY is True (must be False for Axios to read it).\n' +
          '3. Frontend and Backend are on different subdomains and CSRF_COOKIE_DOMAIN is not set correctly.',
      );
    }

    if (
      (status === 401 || (status === 403 && isUnauthorizedMessage)) &&
      !isAuthEndpoint
    ) {
      triggerLoginModal(); // login modal pops up exactly where the user is instead of redirecting to /login
      return Promise.reject(error);
    }

    if (!skipErrorToast && !isAuthEndpoint && !isExpectedLockedResponse) {
      notification.error({
        message: 'Error',
        description: errorDescription,
      });
    }

    return Promise.reject(error);
  },
);

export default Axios;
